'use client';

import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { ExecutionNeoIcon } from '../icons/ExecutionNeoIcon';
import { IconButton } from '../../components/icon-button';
import { Tooltip } from '../../components/tooltip';
import { HStack } from '@terra/ui-styled-system';
import {
  Loading03Icon,
  PlayCircleIcon,
  RefreshIcon,
  StopIcon,
  Delete02Icon,
} from '@terra/ui-icons';
import { WithInfinitetableLoadingCellSkeleton } from '../table/WithInfinitetableLoadingCellSkeleton';
import { useTranslations } from 'next-intl';
import { isArchivedTracePreview } from './is-archived-trace-preview';
import {
  canAbortTrace,
  canRerunTrace,
  canResumeTrace,
  isDeepActiveProbeTrace,
} from './trace-execution-actions';

type TraceCellRendererProps = ICellRendererParams<TracePreviewDto>;

export const TraceActionButtons = React.memo(
  ({
    trace,
    onResumeTraceClick,
    onRerunTraceClick,
    onAbortTraceClick,
    onDeleteTraceClick,
    resumingTraceIds,
    rerunningTraceIds,
    abortingTraceIds,
  }: {
    trace: TracePreviewDto;
    onResumeTraceClick?: (trace: TracePreviewDto) => void;
    onRerunTraceClick?: (trace: TracePreviewDto) => void;
    onAbortTraceClick?: (trace: TracePreviewDto) => void;
    onDeleteTraceClick?: (trace: TracePreviewDto) => void;
    resumingTraceIds?: ReadonlySet<string>;
    rerunningTraceIds?: ReadonlySet<string>;
    abortingTraceIds?: ReadonlySet<string>;
  }) => {
    const tLifecycle = useTranslations('designSystem.lifecycle');
    const tActions = useTranslations('designSystem.executions.actions');
    const archivedTooltip = tLifecycle('archivedExecutionActionTooltip');

    const isDeepActiveProbe = isDeepActiveProbeTrace(trace);
    const rerunLabel = isDeepActiveProbe
      ? tActions('rerunDeepActiveProbe')
      : 'Execute Neo';
    const rerunningLabel = isDeepActiveProbe
      ? tActions('rerunningDeepActiveProbe')
      : 'Executing Neo...';
    const isResuming = Boolean(resumingTraceIds?.has(trace.threadId));
    const isRerunning = Boolean(rerunningTraceIds?.has(trace.threadId));
    const isAborting = Boolean(abortingTraceIds?.has(trace.threadId));
    const isArchived = isArchivedTracePreview(trace);
    const isActionDisabled = isArchived;

    return (
      <HStack gap={0} alignItems="center" h="full">
        {canResumeTrace(trace) && onResumeTraceClick && (
          <Tooltip
            title={
              isArchived
                ? archivedTooltip
                : isResuming
                  ? 'Resuming...'
                  : 'Resume execution'
            }
          >
            <IconButton
              aria-label="Resume execution"
              size="xs"
              variant="ghost"
              disabled={isResuming || isActionDisabled}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (isActionDisabled) {
                  return;
                }

                onResumeTraceClick(trace);
              }}
            >
              {isResuming ? (
                <Loading03Icon
                  size={14}
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              ) : (
                <PlayCircleIcon size={14} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {canRerunTrace(trace) && onRerunTraceClick && (
          <Tooltip
            title={
              isArchived
                ? archivedTooltip
                : isRerunning
                  ? rerunningLabel
                  : rerunLabel
            }
          >
            <IconButton
              aria-label={rerunLabel}
              size="xs"
              variant="ghost"
              disabled={isRerunning || isActionDisabled}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (isActionDisabled) {
                  return;
                }

                onRerunTraceClick(trace);
              }}
            >
              {isRerunning ? (
                <Loading03Icon
                  size={14}
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              ) : isDeepActiveProbe ? (
                <RefreshIcon size={14} />
              ) : (
                <ExecutionNeoIcon size={14} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {canAbortTrace(trace) && onAbortTraceClick && (
          <Tooltip
            title={
              isArchived
                ? archivedTooltip
                : isAborting
                  ? 'Aborting...'
                  : 'Abort execution'
            }
          >
            <IconButton
              aria-label="Abort execution"
              size="xs"
              variant="ghost"
              disabled={isAborting || isActionDisabled}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (isActionDisabled) {
                  return;
                }

                onAbortTraceClick(trace);
              }}
            >
              {isAborting ? (
                <Loading03Icon
                  size={14}
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              ) : (
                <StopIcon size={14} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {onDeleteTraceClick && (
          <Tooltip title={isArchived ? archivedTooltip : 'Delete execution'}>
            <IconButton
              aria-label="Delete execution"
              size="xs"
              variant="ghost"
              disabled={isActionDisabled}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (isActionDisabled) {
                  return;
                }

                onDeleteTraceClick(trace);
              }}
            >
              <Delete02Icon size={14} />
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    );
  },
);
TraceActionButtons.displayName = 'TraceActionButtons';

export function TracesTableActionsCell({
  params,
  ...handlers
}: Omit<React.ComponentProps<typeof TraceActionButtons>, 'trace'> & {
  params: TraceCellRendererProps;
}) {
  if (!params.data) {
    return <WithInfinitetableLoadingCellSkeleton params={params} />;
  }

  return <TraceActionButtons trace={params.data} {...handlers} />;
}
